import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Typography, Stack, Button } from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import { useSelector } from "react-redux";
import { UsersThree } from "@phosphor-icons/react";
import { useTheme } from "@mui/styles";
import * as selectors from "selectors";
import { useGlobalToast } from "components/GlobalToastProvider";
import { useRemoveTeamMember } from "hooks/api/useRemoveTeamMember";
import { useInviteMember } from "hooks/api/useInviteMember";
import { useDeleteTeamMemberInvitation } from "hooks/api/useDeleteTeamMemberInvitation";
import { useGetTeams } from "hooks/api/useGetTeams";
import { useGetTeamMembers } from "hooks/api/useGetTeamMembers";
import { useGetTeamRoles } from "hooks/api/useGetTeamRoles";
import { useUpdateTeamMembers } from "hooks/api/useUpdateTeamMembers";
import { useSaveNewTeamMember } from "hooks/api/useSaveNewTeamMember";
import { useUpdateTeamUser } from "hooks/api/useUpdateTeamUser";
import { useUpdateUserEmail } from "hooks/api/useUpdateUserEmail";
import SectionWrapper from "../../Widgets/SectionWrapper";
import ViewFields from "./ViewFields";
import { TeamMember } from "./interface";
import { ANLoadingScreen } from "@answersnow/pkg-theme";
import CollapsibleMemberView from "./CollapsibleMemberView";
import SidePanelDrawer from "../../Widgets/SidePanelDrawer";
import { useCustomerDetails } from "hooks/useCustomerDetails";
import GuardianRequiredDialog from "../../Widgets/TeamSection/GuardianRequiredDialog";
import AddTeamMemberDialog, { AddTeamMemberFormValues } from "./AddTeamMemberDialog";
import EditTeamMemberDialog from "./EditTeamMemberDialog";
import { GET_TEAM_MEMBERS, GET_USER_BY_ID } from "@constants/reactQueryKeys";
import { useQueryClient } from "react-query";
import ProviderFormDialog, {
  ProviderFormDialogType,
} from "../Providers/ProviderFormDialog";

// Helper function to format team members for the form
const teamMembersFormatted = (teamMembers: any[]) => {
  return teamMembers?.map((member: any) => {
    return {
      id: member.id,
      userId: member.userId,
      name: member.name,
      email: member.email,
      relationshipType: member.roleId,
      isLegalGuardian: member.legalGuardian,
      isDesignatedCaregiver: member.documentSigner,
      hasAcceptedInvite: member.hasAcceptedInvite,
      expirationDate: member.expirationDate,
      invitationGuid: member.invitationGuid,
      preferredLanguage: member.preferredLanguage,
    };
  });
};

export const CareTeam: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const theme: any = useTheme();
  const [editMode, setEditMode] = useState(false);
  const [openPanel, setOpenPanel] = useState(false);
  const [modePanel, setModePanel] = useState<"view" | "edit">("view");
  const [currentMember, setCurrentMember] = useState<any>(null);
  const { showToast } = useGlobalToast();
  const userId = useSelector(selectors.getUserId);
  const isCaregiver = useSelector(selectors.isCaregiver);
  const userPermissions = useSelector(selectors.getUserPermissionsList) as Record<
    string,
    boolean | undefined
  >;
  const canEdit = !isCaregiver && Boolean(userPermissions?.edit_client_info);

  const { data: customerDetails, isLoading: customerLoading } = useCustomerDetails(clientId);

  const { teams, teamsLoading } = useGetTeams(clientId ? { clientIds: clientId } : null);
  const teamId = teams?.[0]?.id;
  const teamName = teams?.[0]?.name || "Team";

  const {
    teamMembers,
    teamMembersLoading,
    refetch: refetchTeamMembers,
  } = useGetTeamMembers({ teamIds: teamId });

  const { teamRoles, teamRolesLoading } = useGetTeamRoles({ teamIds: teamId });
  const { updateTeamMembresMutation, mutateAsync: updateTeamMembersAsync } = useUpdateTeamMembers();

  const { removeTeamMemberMutation } = useRemoveTeamMember();
  const { inviteMemberMutation } = useInviteMember();
  const { deleteTeamMemberInvitationMutation } = useDeleteTeamMemberInvitation();
  const { saveNewTeamMemberMutation, isLoading: isSavingNewMember } = useSaveNewTeamMember();
  const { mutateAsync: updateTeamUserAsync } = useUpdateTeamUser();
  const { updateUserEmailMutationAsync } = useUpdateUserEmail();
  const queryClient = useQueryClient();

  const [openGuardianDialog, setOpenGuardianDialog] = useState(false);
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [editMemberDialogOpen, setEditMemberDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [roiMember, setRoiMember] = useState<TeamMember | null>(null);

  useEffect(() => {
    if (currentMember && teamMembers) {
      const updatedMember = teamMembers.find(
        (m) => m.id === currentMember.id || m.userId === currentMember.userId
      );
      if (updatedMember) {
        setCurrentMember(updatedMember);
      }
    }
  }, [teamMembers]);

  const formik = useFormik({
    initialValues: {
      teamSection: {
        teamId: teamId || "",
        teamMembers: teamMembersFormatted(teamMembers) || [],
      },
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      if (!teamId) return;

      const teamMembersData = values.teamSection.teamMembers
        .filter((member) => member.hasAcceptedInvite)
        .map((member) => ({
          id: member.id,
          roleId: member.relationshipType,
          legalGuardian: member.isLegalGuardian,
          documentSigner: member.isDesignatedCaregiver,
        }));

      updateTeamMembresMutation(
        { teamId, teamMembers: teamMembersData },
        {
          onSuccess: () => {
            refetchTeamMembers();
            setEditMode(false);
            showToast({ message: "Team members updated successfully" });
          },
        }
      );
    },
  });

  const hasLegalGuardian = (values: any, emailToExclude: string) => {
    return values.teamSection?.teamMembers
      ?.filter((row: any) => row.email !== emailToExclude)
      .some((row: any) => row.isLegalGuardian);
  };

  const handleOpenMemberPanel = (memberId: string | number, mode = "view") => {
    const member = teamMembers.find((m) => m.id === memberId || m.userId === memberId);

    if (!member) {
      return;
    }

    if (mode === "edit") {
      const formatted = formik.values.teamSection.teamMembers.find(
        (m) => m.id === member.id || m.userId === member.userId
      );
      setEditingMember(formatted || member);
      setEditMemberDialogOpen(true);
      return;
    }

    setCurrentMember(member);
    setModePanel("view");
    setOpenPanel(true);
  };

  const handleCloseMemberPanel = () => {
    setOpenPanel(false);
    setCurrentMember(null);
  };

  const handleRemoveMember = ({
    userId,
    name,
    email,
    invitationGuid,
  }: TeamMember) => {
    if (!hasLegalGuardian(formik.values, email)) {
      setOpenGuardianDialog(true);
      return;
    }

    const initialDesignatedCaregiver = formik.initialValues.teamSection.teamMembers.find(
      (member) => member.isDesignatedCaregiver
    );

    if (initialDesignatedCaregiver?.userId === userId) {
      showToast({ message: `Please save your changes before removing the team member` });
      return;
    }

    const displayName = name || email;

    const onSuccess = () => {
      refetchTeamMembers();
      showToast({
        message: `${displayName} was successfully removed from the ${teamName}`,
      });
    };

    if (userId) {
      removeTeamMemberMutation({ teamId: teamId, userId }, { onSuccess });
    } else {
      deleteTeamMemberInvitationMutation({ invitationGuid }, { onSuccess });
    }
  };

  const handleResendInvite = ({ email }: { email: string }) => {
    inviteMemberMutation(
      { teamId, email, clientId },
      {
        onSuccess: () => {
          refetchTeamMembers();
          showToast({
            message: `Invitation successfully sent`,
          });
        },
      }
    );
  };

  const handleCreateROI = (member: TeamMember) => {
    if (member.userId == null) {
      showToast({
        message: "Cannot create a Release of Information form without a linked user record",
        errorState: true,
      });
      return;
    }

    setRoiMember(member);
  };

  const handleROIChanges = (roiCreated?: boolean) => {
    if (roiCreated) {
      showToast({ message: "Release of Information successfully created" });
    }
    setRoiMember(null);
  };

  const canViewMemberDetail = (currentMember: TeamMember) => {
    if (!currentMember?.userId) return false;
    const isOwnDetails = isCaregiver && currentMember.userId === userId;
    if (isOwnDetails || !isCaregiver) return true;
    return false;
  };

  const handleEditMemberSubmit = async (changedValues: any) => {
    if (!editingMember) return;

    const {
      email: newEmail,
      relationshipType,
      isLegalGuardian,
      isDesignatedCaregiver,
      preferredLanguage,
      ...userData
    } = changedValues;

    let hasError = false;
    setIsEditSubmitting(true);

    if (newEmail) {
      if (!editingMember.userId) {
        showToast({ message: "Cannot update email for members without an account", errorState: true });
        hasError = true;
      } else {
        try {
          await updateUserEmailMutationAsync({ userId: editingMember.userId, newEmail });
        } catch {
          showToast({ message: "Failed to update email", errorState: true });
          hasError = true;
        }
      }
    }

    if (Object.keys(userData).length > 0 && editingMember.userId) {
      try {
        await updateTeamUserAsync({ userId: editingMember.userId, data: userData });
      } catch {
        showToast({ message: "Failed to update user information", errorState: true });
        hasError = true;
      }
    }

    if (
      relationshipType !== undefined ||
      isLegalGuardian !== undefined ||
      isDesignatedCaregiver !== undefined ||
      preferredLanguage !== undefined
    ) {
      const updatedMember = {
        id: editingMember.id,
        roleId: relationshipType ?? editingMember.relationshipType,
        legalGuardian: isLegalGuardian ?? editingMember.isLegalGuardian,
        documentSigner: isDesignatedCaregiver ?? editingMember.isDesignatedCaregiver,
        ...(preferredLanguage !== undefined ? { preferredLanguage } : {}),
      };

      const formattedMembers = formik.values.teamSection.teamMembers;
      const teamMembersPayload = formattedMembers.map((member) => {
        if (member.id === editingMember.id) {
          return updatedMember;
        }

        if (updatedMember.documentSigner === true && member.isDesignatedCaregiver === true) {
          return {
            id: member.id,
            roleId: member.relationshipType,
            legalGuardian: member.isLegalGuardian,
            documentSigner: false,
          };
        }

        return {
          id: member.id,
          roleId: member.relationshipType,
          legalGuardian: member.isLegalGuardian,
          documentSigner: member.isDesignatedCaregiver,
        };
      });

      try {
        await updateTeamMembersAsync({ teamId, teamMembers: teamMembersPayload });
      } catch {
        showToast({ message: "Failed to update team member roles", errorState: true });
        hasError = true;
      }
    }

    setIsEditSubmitting(false);

    if (hasError) throw new Error("One or more updates failed");

    showToast({ message: "Member updated successfully" });
    setEditMemberDialogOpen(false);
    setEditingMember(null);
    queryClient.invalidateQueries([GET_TEAM_MEMBERS, { teamIds: teamId }]);
    if (editingMember.userId) {
      queryClient.invalidateQueries([GET_USER_BY_ID, editingMember.userId]);
    }
    refetchTeamMembers();
  };

  const handleSendInviteFromEdit = () => {
    if (!editingMember?.email) return;
    inviteMemberMutation(
      { teamId, email: editingMember.email, clientId },
      {
        onSuccess: () => {
          refetchTeamMembers();
          setEditMemberDialogOpen(false);
          setEditingMember(null);
          showToast({ message: "Invitation successfully sent" });
        },
      }
    );
  };

  const handleEdit = () => setEditMode(true);
  const handleCancel = () => {
    formik.resetForm();
    setEditMode(false);
  };
  const handleSave = () => {
    formik.handleSubmit();
  };

  if (teamsLoading || teamMembersLoading || teamRolesLoading || customerLoading) {
    return <ANLoadingScreen />;
  }

  if (!customerDetails?.name) {
    return (
      <Stack sx={{ p: 5 }}>
        <Typography>No client information found</Typography>
      </Stack>
    );
  }

  return (
    <FormikProvider value={formik}>
      <SectionWrapper
        title="External Support Team"
        description={`${customerDetails?.name}'s external support team, including family members, caregivers, and other professionals, involved in their care.`}
        headingBottomSpacing="24px"
        sectionPadding={0}
        editMode={editMode}
        onEdit={canEdit ? handleEdit : undefined}
        onCancel={handleCancel}
        onSave={handleSave}
        saveDisabled={!formik.dirty}
        hideButtons={true}
        id="client-team-section"
      >
        <Stack gap="24px">
          <ViewFields
            teamMembers={formik.values.teamSection?.teamMembers || []}
            teamRoles={teamRoles}
            onOpenMemberPanel={handleOpenMemberPanel}
            canViewMemberDetail={canViewMemberDetail}
            canEdit={canEdit}
            onRemoveMember={handleRemoveMember}
            onResendInvite={handleResendInvite}
            onCreateROI={handleCreateROI}
            userPermissions={userPermissions}
          />

          {canEdit && (
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={(event) => {
                event.currentTarget.blur();
                setAddMemberDialogOpen(true);
              }}
              sx={{ alignSelf: "flex-start" }}
            >
              Add New Team Member
            </Button>
          )}
        </Stack>
      </SectionWrapper>

      <SidePanelDrawer
        open={openPanel && Boolean(currentMember?.userId)}
        onClose={handleCloseMemberPanel}
        width={400}
        title={currentMember?.name}
        bodyPadding={0}
        pencilAction={canEdit ? () => {
          const formatted = formik.values.teamSection.teamMembers.find(
            (m) => m.id === currentMember?.id || m.userId === currentMember?.userId
          );
          setOpenPanel(false);
          setEditingMember(formatted || currentMember);
          setEditMemberDialogOpen(true);
        } : undefined}
        icon={<UsersThree weight="duotone" size="20px" color={theme.palette.action.active} />}
      >
        {currentMember?.userId && (
          <CollapsibleMemberView
            currentMember={currentMember}
            mode={modePanel}
            setOpen={setOpenPanel}
            userPermissions={userPermissions}
            refetchTeamMembers={refetchTeamMembers}
            setEditMode={setEditMode}
            setModePanel={setModePanel}
            showEditButton={() => {}}
            teamRoles={teamRoles}
            teamId={teamId}
            allTeamMembers={teamMembers}
          />
        )}
      </SidePanelDrawer>

      <GuardianRequiredDialog
        open={openGuardianDialog}
        onClose={() => setOpenGuardianDialog(false)}
      />

      <AddTeamMemberDialog
        open={addMemberDialogOpen}
        onClose={() => setAddMemberDialogOpen(false)}
        onSubmit={(values: AddTeamMemberFormValues) => {
          saveNewTeamMemberMutation(
            {
              teamId,
              clientId,
              email: values.email,
              firstName: values.firstName,
              lastName: values.lastName,
              phoneNumber: values.phoneNumber,
              roleId: values.relationshipType,
              legalGuardian: values.isLegalGuardian,
              documentSigner: values.isDesignatedCaregiver,
              dateOfBirth: values.dateOfBirth,
              gender: values.gender,
              preferredLanguage: values.preferredLanguage,
              address1: values.address1,
              address2: values.address2,
              country: values.country,
              state: values.state,
              city: values.city,
              zipCode: values.zipCode,
              timezone: values.timezone,
              sendInvitation: values.sendInvitation,
              generateRoi: userPermissions?.create_phi_release_request
                ? values.generateRoi
                : false,
            },
            {
              onSuccess: () => {
                refetchTeamMembers();
                setAddMemberDialogOpen(false);
                showToast({
                  message: values.sendInvitation
                    ? `${values.firstName} ${values.lastName} has been added and an invitation was sent`
                    : `${values.firstName} ${values.lastName} has been added to the team`,
                });
              },
            }
          );
        }}
        teamRoles={teamRoles}
        existingEmails={teamMembers?.map((m: any) => m.email) || []}
        clientName={customerDetails?.name}
        isSubmitting={isSavingNewMember}
        userPermissions={userPermissions}
      />

      {roiMember && (
        <ProviderFormDialog
          open
          closeDialog={() => setRoiMember(null)}
          formDialogType={ProviderFormDialogType.roi}
          clientId={clientId}
          providerFormValues={{ teamMemberId: roiMember.id, teamMemberUserId: roiMember.userId }}
          onProviderChanges={handleROIChanges}
          userPermissions={userPermissions}
        />
      )}

      <EditTeamMemberDialog
        open={editMemberDialogOpen}
        onClose={() => {
          setEditMemberDialogOpen(false);
          setEditingMember(null);
        }}
        onSubmit={handleEditMemberSubmit}
        member={editingMember}
        teamRoles={teamRoles}
        allTeamMembers={formik.values.teamSection?.teamMembers || []}
        isSubmitting={isEditSubmitting}
        onSendInvite={handleSendInviteFromEdit}
        userPermissions={userPermissions}
      />
    </FormikProvider>
  );
};

export default CareTeam;
